import { StockItemType } from './../../stock/StockItems/StockItems.types';
import { Moment } from 'moment';
import { PageRequest } from '@/core/request/PageRequest';
import { PageResult } from '@/core/request/PageResult';

export enum InventoryStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
  PROCESSING = 'PROCESSING',
  ERROR = 'ERROR',
  IMPORTING = 'IMPORTING',
}

export const InventoryStatusColor = {
  [InventoryStatus.DONE]: 'green',
  [InventoryStatus.IN_PROGRESS]: 'blue',
  [InventoryStatus.PROCESSING]: 'yellow',
  [InventoryStatus.CANCELLED]: 'red',
  [InventoryStatus.IMPORTING]: 'orange',
};

export enum InventoryReason {
  END_OF_PERIOD = 'END_OF_PERIOD',
  TAX_CHANGE = 'TAX_CHANGE',
  COMMIT_REGISTRATION = 'COMMIT_REGISTRATION',
  PAYMENT_SCHEME_CHANGE = 'PAYMENT_SCHEME_CHANGE',
  MANDATE = 'MANDATE',
}

export enum ProductType {
  PART = 'PART',
  CONSUMABLE = 'CONSUMABLE',
  ALL = 'ALL',
}

export enum CheckedType {
  all = 'all',
  checked = 'true',
  unchecked = 'false',
}

export enum FinalizeOrNextCount {
  finalizeInventory = 'finalizeInventory',
  nextCount = 'nextCount',
}

export const ProductTypeLabels = {
  [ProductType.PART]: {
    all: 'todas as',
    pascalcase: 'Peças',
    lowercase: 'peças',
  },
  [ProductType.CONSUMABLE]: {
    all: 'todos os',
    pascalcase: 'Insumos',
    lowercase: 'insumos',
  },
  [ProductType.ALL]: {
    all: 'todas as',
    pascalcase: 'Peças e insumos',
    lowercase: 'peças e insumos',
  },
};

export interface Inventory {
  company: { id: number; name: string };
  date: Moment;
  reason: InventoryReason;
  id: number;
  status: InventoryStatus;
  matrixId?: number;
  productType: ProductType;
  currentCount: {
    countNumber?: number;
    finishDate: string;
    id: number;
    status: InventoryStatus;
  };
  processPrices?: boolean;
}

export interface InventoryItemAllResponse {
  id: number;
  type: 'PART' | 'CONSUMABLE';
  consumableId: number;
  partId: number;
  price: number;
  checked: true;
  countedQuantity: number;
  oldQuantity: number;
}

export interface ListInventoryParams extends PageRequest {
  matrixId?: number;
  allItems?: boolean;
  companyId?: number;
  companyName: string;
  date: string;
  reason: InventoryReason;
  status: InventoryStatus;
}

export interface InventoryFormValues {
  company: { id: number; name: string };
  reason: InventoryReason;
  date: Moment;
  productType: ProductType;
  items: InventoryItemsResponse;
  amount: number;
}

export interface InventoryFilterProps extends PageRequest {
  companyId?: number;
  date?: string;
  reason?: InventoryReason;
  status?: InventoryStatus;
  search?: string;
  companyName?: string;
}

export interface CreateInventoryRequest {
  reason: InventoryReason;
  company: {
    id: number;
  };
  date: string;
  productType: ProductType;
}

export interface UpdateInventoryRequest {
  id: number;
  date?: string;
  reason?: InventoryReason;
}

export interface ListItemInventoryParams {
  id?: number;
  nameFilterType?: string;
  matrixId?: number;
  countId?: number;
  active?: boolean;
  barCode?: string;
  inventoryId?: number;
  manufacturingCode?: string;
  name?: string;
  sku?: string;
  companyCode?: string;
  checked?: boolean;
  orderColumns?: string;
  nextToken?: string;
  rows?: number;
  order?: 'ASC' | 'DESC';
  search?: string;
  sortByUpdatedAt?: boolean;
}

export interface ListItemInventoryResponse {
  item?: InventoryItemsResponse;
  stock: number;
  stockItemsDTO: StockItemsDTO;
}

export type InventoryItemListResponse =
  PageResult<ListItemInventoryResponse> & {
    currentPage: number;
    itemsCount: number;
    pagedConsumablesIds: string;
    pagedPartsIds: string;
  };

export interface InventoryItemProductDTO {
  barCode: string;
  id: number;
  manufacturingCode: string;
  name: string;
  sku: string;
  type: ProductType;
  companyCode: string;
}

export interface InventoryItemsResponse {
  id: number;
  type: ProductType;
  consumableId: number;
  partId: number;
  price: number;
  checked: boolean;
  countedQuantity: number;
}

export interface StockItemsPrice {
  company: {
    id: number;
    name: string;
  };
  cost: number;
  price: number;
  realCost: number;
}

export interface StockItemsStock {
  company: {
    id: number;
    name: string;
  };
  stock: number;
  reserved: number;
  availableStock: number;
}

export interface StockItemsDTO {
  partId: number;
  consumableId: number;
  barcode: string;
  originalCode: string;
  sku: string;
  companyCode: string;
  manufacturingCode: string;
  location: string;
  totalStock: number;
  prices: StockItemsPrice[];
  stock: StockItemsStock[];
  description: string;
}

export interface LaunchItemsRequest {
  id?: number;
  type: ProductType;
  consumableId?: number;
  partId?: number;
  price?: number;
  checked: boolean;
  countedQuantity: number;
}

export interface AlterStatusInventory {
  id: number;
  status: InventoryStatus;
  processPrices?: boolean;
  populateInventory?: boolean;
  populateOnlyWithStock?: boolean;
}

export interface PageResultInventory {
  nextToken: string;
  data: ListItemInventoryResponse[];
  pagedConsumablesIds?: string;
  pagedPartsIds?: string;
}

export const CONVERT_ITEM_TYPE = {
  [ProductType.PART]: StockItemType.Part,
  [ProductType.CONSUMABLE]: StockItemType.Consumable,
  [ProductType.ALL]: undefined,
};

export interface ValidateInventoryResponse {
  valid: boolean;
  hasAtLeastOneCheckedItem: boolean;
  itemsWithLessCountedThanReserved: {
    name: string;
    countedQuantity: number;
    totalReservedQuantity: number;
  }[];
}
